import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import classNames from 'classnames';
import Button from 'components/Button/Button';
import { Api } from 'api/otvety';
import PostMarusiaVote from 'views/Questions/api/postMarusiaVote';
import postMarusiaComplain from 'views/Questions/api/postMarusiaComplain';
import MarusiaHelpModal from 'components/Modals/MarusiaHelpModal/MarusiaHelpModal';
import getQuerySSR from 'utils/getQuerySSR';
import { appAuthContext } from 'context/AuthContext';
import showNotification from 'utils/showNotification';
import useClickStreamEvent from 'utils/clickstream/useClickStreamEvent';
import AuthModalController from 'components/AuthModalController/AuthModalController';
import LatexContainer from '../LatexContainer/LatexContainer';
import { LikeIcon } from './LikeIcon';
import styles from './AIAnswerCard.module.css';

type Props = {
  marusiaAnswer: Api.GetMarusiaData200Response.MarusiaAnswer;
  parentRef: React.MutableRefObject<HTMLDivElement | null>;
  hasAnswers: boolean;
};

const AIAnswerCard = ({ marusiaAnswer, parentRef, hasAnswers }: Props) => {
  const { user } = useContext(appAuthContext);
  const answerContainer = useRef<HTMLDivElement | null>(null);

  const { text, already_voted, already_complained } = marusiaAnswer;
  const formattedText = useMemo(() => text.replace(/\n\n/g, '\n'), [text]);

  const router = useRouter();
  const { sendEvent } = useClickStreamEvent();
  const [userVoted, setUserVoted] = useState(already_voted);
  const [expandedAnswer, setExpandedAnswer] = useState(false);
  const [showRegModal, setShowRegModal] = useState(false);
  const [isReported, setIsReported] = useState(already_complained);
  const [showMarusiaHelperModal, setShowMarusiaHelperModal] = useState(false);

  const ms = 125;
  const lettersCount = 25;
  const localStorageName = 'opened_ai_answers';
  const visibleAnswerHalf = formattedText.split('').slice(0, 100).join('');
  const unvisibleAnswerHalf = formattedText
    .split('')
    .slice(100, formattedText.length)
    .join('');

  const [answer, setAnswer] = useState(visibleAnswerHalf);

  const slug = getQuerySSR(router.query.slug);

  useEffect(() => {
    sendEvent({
      eventType: 'display',
      eventInfo: `otvety_marusia`,
      customDimentions: {
        cd1: JSON.stringify({ hasAnswer: hasAnswers }),
      },
    });
    if (!slug || !user?.data) return;
    if (localStorage.getItem(localStorageName)?.split(',').includes(slug)) {
      expandImmediately();
    }
  }, [slug, user]);

  if (!slug) return null; // just typecheck

  const voteForAnswerHandler = async () => {
    if (userVoted) return;
    if (!user) {
      setShowRegModal(true);
    }
    const postVoteData = await PostMarusiaVote(slug);
    if (postVoteData.success) {
      setUserVoted(true);
    }
  };

  const submitReportHandler = async () => {
    if (isReported) return;
    if (!user) {
      return showNotification(
        'info',
        'Чтобы отправить жалобу, войди или зарегистрируйся',
      );
    }
    setIsReported(true);
    const postMarusiaData = await postMarusiaComplain(slug);
    if (postMarusiaData.success) {
      setUserVoted(true);
    }
  };

  const splitStringIntoChunks = (str: string, chunkSize: number) => {
    const result = [];
    for (let i = 0; i < str.length; i += chunkSize) {
      result.push(str.slice(i, i + chunkSize));
    }
    return result;
  };

  const expandWithGhostAndLetters = () => {
    let i = 0;
    const answerArray = splitStringIntoChunks(unvisibleAnswerHalf, lettersCount);
    const intervalId = setInterval(() => {
      if (i < answerArray.length) {
        i++;
        setAnswer((prev) => prev + answerArray[i - 1]);
      } else {
        clearInterval(intervalId);
      }
    }, ms);
  };

  const expandImmediately = () => {
    if (answerContainer?.current) {
      answerContainer.current.style.transition = 'unset';
    }

    setExpandedAnswer(true);
    setAnswer(formattedText);
  };

  const expandAnswer = () => {
    sendEvent({
      eventType: 'click',
      eventInfo: `otvety_marusia`,
      customDimentions: {
        cd1: JSON.stringify({ hasAnswer: hasAnswers }),
      },
    });

    if (!user) {
      return setShowRegModal(true);
    } else {
      const openedAnswers = localStorage.getItem(localStorageName)?.split(',');

      if (openedAnswers && openedAnswers?.length > 10) {
        localStorage.removeItem(localStorageName);
        localStorage.setItem(localStorageName, slug.toString() + ',');
      } else {
        localStorage.setItem(
          localStorageName,
          openedAnswers
            ? `${openedAnswers?.join(',')}${slug.toString()},`
            : `${slug.toString()},`,
        );
      }
    }

    parentRef.current?.scrollIntoView({ behavior: 'smooth' });
    expandWithGhostAndLetters();
    setExpandedAnswer(true);
  };

  const helpModalHandler = () => {
    setShowMarusiaHelperModal(true);
    sendEvent({
      eventType: 'display',
      eventInfo: `otvety_marusia_modal`,
      customDimentions: {
        cd1: JSON.stringify({ hasAnswer: hasAnswers }),
      },
    });
  };

  return (
    <article className={styles.card}>
      <div className={styles.user}>
        <div className={styles.user__avatar}>
          <Image
            alt="Аватар"
            src={'https://static.uchi.ru/files/otvety/marusya_pfp.png'}
            width={26}
            height={32}
          />
        </div>
        <div className={styles.marusia}>
          <span className={styles.marusia__title}>Маруся — умный бот</span>
          <span className={styles.marusia__subtitle}>
            Это экспериментальная функция.{' '}
            <a className={styles.marusia__subtitle_link} onClick={helpModalHandler}>
              Что&nbsp;это&nbsp;значит?
            </a>
          </span>
        </div>
      </div>
      <div
        className={classNames(styles.card__content, {
          [styles.expanded]: expandedAnswer,
        })}
        ref={answerContainer}
      >
        <div
          className={classNames(styles.card__content_body, {
            [styles.card__content_show]: expandedAnswer,
          })}
        >
          <LatexContainer text={answer} />
          <div className={styles.card__content_shadow} />
        </div>

        {expandedAnswer && (
          <div className={styles.card__content_hidden_body}>
            <LatexContainer text={answer} />
          </div>
        )}

        {isReported && expandedAnswer && (
          <div className={styles.card__content_reported__text}>
            Мы рассматриваем твою жалобу
          </div>
        )}
        <button className={styles.card__content_button} onClick={expandAnswer}>
          Показать ответ полностью
          <span className={styles.card__content_button__inner}></span>
        </button>
      </div>
      {expandedAnswer && (
        <div className={styles.card__controll}>
          <Button
            className={`${styles.thanks_btn} ${
              userVoted ? styles.thanks_btn_active : ''
            }`}
            text=""
            disabled={userVoted}
            onClick={voteForAnswerHandler}
            type={'default'}
          >
            <>
              <LikeIcon className={styles.like_icon} />
              <span className={styles.thanks_btn__text}>Спасибо, Маруся</span>
            </>
          </Button>
          <Button
            className={styles.report_btn}
            text=""
            onClick={submitReportHandler}
            type="transparent"
          >
            <Image
              alt="Пожаловаться"
              src="https://static.uchi.ru/files/otvety/report_flag.svg"
              width={24}
              height={24}
            />
          </Button>
        </div>
      )}
      {showRegModal && (
        <AuthModalController
          handleClose={() => {
            setShowRegModal(false);
          }}
          entryPoint={'open_ai_answer'}
        />
      )}
      {showMarusiaHelperModal && (
        <MarusiaHelpModal onClose={() => setShowMarusiaHelperModal(false)} />
      )}
    </article>
  );
};

AIAnswerCard.displayName = 'AIAnswerCard';

export default AIAnswerCard;
